import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const TABS = [["overview", "Overview", "LayoutDashboard"], ["pipeline", "Pipeline", "GitMerge"], ["staff", "Staff Performance", "Users"], ["departments", "Departments", "Building2"], ["intelligence", "Business Intelligence", "BarChart3"], ["reports", "Reports", "FileBarChart"]];
const PIE = ["#F5A623", "#1f6feb", "#10b981", "#8b5cf6", "#ef4444", "#06b6d4", "#f59e0b", "#64748b"];
const SEV = { high: "border-l-rose-500 bg-rose-50/50", medium: "border-l-amber-500 bg-amber-50/40", low: "border-l-slate-300 bg-white" };

function KpiCard({ label, value, icon, tone = "text-slate-800", onClick, testid }) {
  const Icon = Icons[icon] || Icons.Activity;
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`bg-white rounded-xl border border-slate-200 shadow-sm p-4 text-left ${onClick ? "hover:border-slate-300 hover:shadow transition-all" : "cursor-default"}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500"><Icon className="h-4 w-4 text-[#F5A623]" />{label}</div>
      <div className={`text-2xl font-bold mt-1.5 ${tone}`}>{value}</div>
    </button>
  );
}

function Section({ title, children, icon }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="space-y-2.5">
      <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Icon className="h-4 w-4 text-slate-400" />{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">{children}</div>
    </div>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get(`/executive/search?q=${encodeURIComponent(q)}`).then((r) => { setRes(r.data.results); setOpen(true); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)} data-testid="exec-global-search"
        placeholder="Search clients, engagements, invoices, staff…" className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
      {open && res && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto" data-testid="exec-search-results">
          {res.length === 0 ? <div className="p-4 text-sm text-slate-400 text-center">No results.</div> :
          res.map((r, i) => (
            <button key={i} onClick={() => { navigate(r.link); setOpen(false); setQ(""); }} data-testid={`exec-search-result-${i}`} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between border-b border-slate-50 last:border-0">
              <div><div className="text-sm text-slate-800">{r.title}</div>{r.subtitle && <div className="text-xs text-slate-400">{r.subtitle}</div>}</div>
              <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const [ov, setOv] = useState(null);
  const [attention, setAttention] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [staff, setStaff] = useState(null);
  const [depts, setDepts] = useState(null);
  const [bi, setBi] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => { api.get("/executive/overview").then((r) => setOv(r.data)).catch(() => setOv({})); api.get("/executive/attention").then((r) => setAttention(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "pipeline" && !pipeline) api.get("/executive/pipeline").then((r) => setPipeline(r.data.stages));
    if (tab === "staff" && !staff) api.get("/executive/staff-performance").then((r) => setStaff(r.data.items));
    if (tab === "departments" && !depts) api.get("/executive/departments").then((r) => setDepts(r.data.departments));
    if (tab === "intelligence" && !bi) api.get("/executive/business-intelligence").then((r) => setBi(r.data));
    if (tab === "reports" && !reports) api.get("/executive/reports").then((r) => setReports(r.data));
  }, [tab]); // eslint-disable-line

  const setTab = (t) => setSp(t === "overview" ? {} : { tab: t });

  return (
    <div className="space-y-5" data-testid="executive-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Executive Command Center</p>
          <h1 className="text-2xl font-bold text-slate-900">Firm Performance</h1>
        </div>
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="exec-tabs">
        {TABS.map(([k, label, icon]) => {
          const Icon = Icons[icon];
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`exec-tab-${k}`}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-[#F5A623] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (ov ? <OverviewTab ov={ov} attention={attention} navigate={navigate} /> : <Loader />)}
      {tab === "pipeline" && (pipeline ? <PipelineTab stages={pipeline} navigate={navigate} /> : <Loader />)}
      {tab === "staff" && (staff ? <StaffTab rows={staff} /> : <Loader />)}
      {tab === "departments" && (depts ? <DeptTab depts={depts} /> : <Loader />)}
      {tab === "intelligence" && (bi ? <BiTab bi={bi} /> : <Loader />)}
      {tab === "reports" && (reports ? <ReportsTab reports={reports} /> : <Loader />)}
    </div>
  );
}

const Loader = () => <div className="py-20 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623] inline" /></div>;

function OverviewTab({ ov, attention, navigate }) {
  const b = ov.brief || {};
  return (
    <div className="space-y-6">
      {/* Daily Brief */}
      <div className="bg-gradient-to-r from-[#171B2E] to-[#2a3152] text-white rounded-2xl p-5 shadow-sm" data-testid="exec-daily-brief">
        <div className="flex items-center gap-2 mb-3"><Icons.Sunrise className="h-5 w-5 text-[#F5A623]" /><h2 className="font-semibold">Today's Executive Brief</h2></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[["New Leads", b.new_leads], ["Revenue Received", money(b.revenue_received)], ["Engagements Done", b.engagements_completed], ["Blocked Work", b.blocked_work], ["Urgent Items", b.urgent_items], ["Active Production", b.production_summary?.active]].map(([l, v]) => (
            <div key={l}><div className="text-xs text-slate-300">{l}</div><div className="text-xl font-bold mt-0.5">{v ?? 0}</div></div>
          ))}
        </div>
      </div>

      {/* Attention Required */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="exec-attention">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Icons.AlertTriangle className="h-4 w-4 text-rose-500" />Executive Attention Required</h2>
          <span className="text-xs text-slate-400">{attention?.total || 0} items{attention?.counts?.high ? ` · ${attention.counts.high} high` : ""}</span>
        </div>
        {!attention ? <Loader /> : attention.items.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nothing needs your attention right now. 🎉</p> :
        <div className="space-y-2">
          {attention.items.slice(0, 12).map((it, i) => (
            <button key={i} onClick={() => navigate(it.link)} data-testid={`attention-item-${i}`} className={`w-full text-left border-l-2 ${SEV[it.severity]} rounded-r-lg px-3.5 py-2.5 hover:shadow-sm transition-shadow flex items-center justify-between`}>
              <div><div className="text-sm font-medium text-slate-800">{it.title}</div><div className="text-xs text-slate-500">{it.detail}</div></div>
              <Icons.ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
        </div>}
      </div>

      <Section title="Sales" icon="TrendingUp">
        <KpiCard testid="kpi-new-leads-today" label="New Leads Today" value={ov.sales.new_leads_today} icon="UserPlus" onClick={() => navigate("/crm?lifecycle_status=lead")} />
        <KpiCard label="Leads This Month" value={ov.sales.leads_this_month} icon="Users" />
        <KpiCard label="Meetings Scheduled" value={ov.sales.meetings_scheduled} icon="CalendarClock" />
        <KpiCard testid="kpi-proposals-out" label="Estimates Outstanding" value={ov.sales.proposals_outstanding} icon="FileText" onClick={() => navigate("/sales")} />
        <KpiCard label="Acceptance Rate" value={`${ov.sales.proposal_acceptance_rate}%`} icon="Percent" tone="text-emerald-600" />
        <KpiCard label="Pipeline Value" value={money(ov.sales.pipeline_value)} icon="DollarSign" />
      </Section>

      <Section title="Finance" icon="DollarSign">
        <KpiCard testid="kpi-revenue-month" label="Revenue (MTD)" value={money(ov.finance.revenue_this_month)} icon="TrendingUp" tone="text-emerald-600" />
        <KpiCard label="Revenue Pipeline" value={money(ov.finance.revenue_pipeline)} icon="GitMerge" />
        <KpiCard testid="kpi-ar" label="Accounts Receivable" value={money(ov.finance.accounts_receivable)} icon="Landmark" tone="text-rose-600" onClick={() => navigate("/finance/receivables")} />
        <KpiCard label="Outstanding Invoices" value={ov.finance.outstanding_invoices} icon="Receipt" onClick={() => navigate("/finance/invoices")} />
        <KpiCard label="Collection Rate" value={`${ov.finance.collection_rate}%`} icon="Percent" />
        <KpiCard label="Overdue Accounts" value={ov.finance.overdue_accounts} icon="AlarmClock" tone={ov.finance.overdue_accounts ? "text-rose-600" : "text-slate-800"} />
      </Section>

      <Section title="Production" icon="Factory">
        <KpiCard label="Active Engagements" value={ov.production.active_engagements} icon="Briefcase" onClick={() => navigate("/production?tab=queue")} />
        <KpiCard label="Waiting on Client" value={ov.production.waiting_on_client} icon="Clock" />
        <KpiCard label="Internal Review" value={ov.production.internal_review} icon="SearchCheck" />
        <KpiCard label="Ready to File" value={ov.production.ready_to_file} icon="FileCheck2" />
        <KpiCard label="Completed Today" value={ov.production.completed_today} icon="CheckCircle2" tone="text-emerald-600" />
        <KpiCard label="Avg Production Days" value={ov.production.average_production_days} icon="Timer" />
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-2.5">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Icons.MessagesSquare className="h-4 w-4 text-slate-400" />Communications</h2>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard label="Open" value={ov.communications.open_conversations} icon="MessageSquare" onClick={() => navigate("/communications")} />
            <KpiCard label="Unanswered" value={ov.communications.unanswered_messages} icon="MessageSquareWarning" tone="text-amber-600" onClick={() => navigate("/communications/unanswered")} />
            <KpiCard label="Escalated" value={ov.communications.escalated_conversations} icon="AlertTriangle" tone="text-rose-600" />
            <KpiCard label="Avg Response" value={`${ov.communications.average_response_minutes}m`} icon="Timer" />
          </div>
        </div>
        <div className="lg:col-span-1 space-y-2.5">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Icons.ListChecks className="h-4 w-4 text-slate-400" />Operations</h2>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard label="Docs to Review" value={ov.operations.documents_awaiting_review} icon="FileSearch" onClick={() => navigate("/documents")} />
            <KpiCard label="Organizers Review" value={ov.operations.organizers_awaiting_review} icon="ClipboardCheck" onClick={() => navigate("/ops/organizer-review")} />
            <KpiCard label="Info Requests" value={ov.operations.information_requests} icon="HelpCircle" />
            <KpiCard label="Tasks Overdue" value={ov.operations.tasks_overdue} icon="AlarmClock" tone="text-rose-600" />
          </div>
        </div>
        <div className="lg:col-span-1 space-y-2.5">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Icons.Gauge className="h-4 w-4 text-slate-400" />Firm KPIs</h2>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard label="Active Clients" value={ov.firm_kpis.active_clients} icon="Users" onClick={() => navigate("/crm")} />
            <KpiCard label="Active Engagements" value={ov.firm_kpis.active_engagements} icon="Briefcase" />
            <KpiCard label="Open Tasks" value={ov.firm_kpis.open_tasks} icon="ListTodo" />
            <KpiCard label="SLA Compliance" value={`${ov.firm_kpis.sla_compliance}%`} icon="ShieldCheck" tone="text-emerald-600" />
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-400 italic">{ov.penny_note}</p>
    </div>
  );
}

function PipelineTab({ stages, navigate }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="exec-pipeline">
      {stages.map((s, i) => (
        <button key={i} onClick={() => s.link && navigate(s.link)} data-testid={`pipeline-stage-${i}`} className="w-full flex items-center gap-3 group">
          <div className="w-32 text-sm text-slate-600 text-right shrink-0">{s.stage}</div>
          <div className="flex-1 bg-slate-100 rounded-lg h-12 relative overflow-hidden group-hover:ring-2 group-hover:ring-amber-200">
            <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#4d8bf0] flex items-center px-3 transition-all" style={{ width: `${Math.max(8, (s.count / max) * 100)}%` }}>
              <span className="text-white font-bold text-sm">{s.count}</span>
            </div>
          </div>
          <div className="w-44 text-xs text-slate-500 shrink-0 text-left">
            {s.value != null && <span className="font-medium text-slate-700">{money(s.value)}</span>}
            {s.avg_days != null && <span className="ml-2">{s.avg_days}d avg</span>}
            {s.conversion_rate != null && <span className="ml-2 text-emerald-600">{s.conversion_rate}%</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function StaffTab({ rows }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="exec-staff">
      <table className="w-full text-sm whitespace-nowrap">
        <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">{["Staff", "Role", "Assigned", "Completed", "Open Tasks", "Overdue", "Conversations", "Production", "Avg Turnaround", "Utilization"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} data-testid={`staff-row-${r.user_id}`} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="px-3 py-2.5 font-medium text-slate-800">{r.name}</td>
              <td className="px-3 py-2.5 capitalize text-slate-500">{(r.role || "").replace(/_/g, " ")}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.cases_assigned}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.cases_completed}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.open_tasks}</td>
              <td className="px-3 py-2.5">{r.overdue_tasks > 0 ? <span className="text-rose-600 font-semibold">{r.overdue_tasks}</span> : "0"}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.conversations_assigned}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.production_load}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.avg_turnaround_days != null ? `${r.avg_turnaround_days}d` : "—"}</td>
              <td className="px-3 py-2.5"><div className="flex items-center gap-2"><div className="w-16 bg-slate-100 rounded-full h-2"><div className={`h-2 rounded-full ${r.utilization_pct >= 90 ? "bg-rose-500" : r.utilization_pct >= 60 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${r.utilization_pct}%` }} /></div><span className="text-xs text-slate-500">{r.utilization_pct}%</span></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeptTab({ depts }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exec-departments">
      {depts.map((d) => (
        <div key={d.key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={`dept-${d.key}`}>
          <h3 className="font-semibold text-slate-800">{d.name}</h3>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Workload</span><span className="font-bold text-slate-800">{d.workload}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Open Items</span><span className="text-slate-700">{d.open_items}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">SLA Compliance</span><span className="text-slate-700">{d.sla_compliance != null ? `${d.sla_compliance}%` : "—"}</span></div>
          </div>
          {d.bottleneck && <div className="mt-3 text-xs bg-amber-50 text-amber-700 rounded-lg px-2.5 py-1.5 flex items-center gap-1"><Icons.AlertCircle className="h-3.5 w-3.5" />{d.bottleneck}</div>}
        </div>
      ))}
    </div>
  );
}

function BiTab({ bi }) {
  return (
    <div className="space-y-5" data-testid="exec-intelligence">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Revenue Trend (6 mo)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={bi.revenue_trends}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v / 1000}k`} /><Tooltip formatter={(v) => money(v)} /><Line type="monotone" dataKey="revenue" stroke="#1f6feb" strokeWidth={2.5} dot={{ r: 3 }} /></LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Revenue by Service">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bi.revenue_by_service}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v / 1000}k`} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="value" fill="#F5A623" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Revenue by Country">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart><Pie data={bi.revenue_by_country} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => e.name}>{bi.revenue_by_country.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip formatter={(v) => money(v)} /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Revenue by Team">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bi.revenue_by_team} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Lead Conversion" value={`${bi.lead_conversion}%`} icon="UserCheck" />
        <KpiCard label="Estimate Conversion" value={`${bi.proposal_conversion}%`} icon="FileCheck" />
        <KpiCard label="Avg Engagement Value" value={money(bi.average_engagement_value)} icon="DollarSign" />
        <KpiCard label="Client LTV" value="—" icon="Gem" />
      </div>
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"><h3 className="font-semibold text-slate-800 mb-3 text-sm">{title}</h3>{children}</div>
);

function ReportsTab({ reports }) {
  return (
    <div className="space-y-4" data-testid="exec-reports">
      <p className="text-sm text-slate-500">Export-ready reports. PDF / Excel export is a placeholder pending integration.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.reports.map((r) => (
          <div key={r.key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between" data-testid={`report-${r.key}`}>
            <div className="flex items-center gap-2"><Icons.FileBarChart className="h-5 w-5 text-[#1f6feb]" /><span className="font-medium text-slate-800 text-sm">{r.name}</span></div>
            <div className="flex gap-1">
              {reports.formats.map((f) => (
                <button key={f} disabled data-testid={`report-${r.key}-${f}`} title={`Export ${f.toUpperCase()} (placeholder)`} className="text-[11px] uppercase border border-slate-200 text-slate-400 rounded px-2 py-1 cursor-not-allowed opacity-60">{f}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
